import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Container } from 'reactstrap';
import { InventoryEntities } from 'client/data/models/inventory';
import convexMask from 'site-modules/shared/components/clipped-container/mask-library/usurp-header';
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
import { QuickFilters } from 'site-modules/shared/components/inventory/quick-filters/quick-filters';
import { transformSelectedFacetsForDisplaying } from 'site-modules/shared/utils/inventory/search-filter';
import { SPR_SCROLL_TO_TOP_ANCHOR } from 'site-modules/shared/constants/inventory/routes-constants';

import './usurp-sticky-header.scss';

export const UsurpStickyHeader = memo(
  ({
    facets = [],
    selectedFacets = {},
    suggestedFacets = [],
    onUpdate,
    filteringByFacets = [],
    titleComponent: TitleComponent,
    suggestedFacetsComponent: SuggestedFacetsComponent,
    isMobile = false,
    ...titleComponentProps
  }) => {
    const activeFiltersCount = transformSelectedFacetsForDisplaying(filteringByFacets).length;
    const { onFiltersBtnClick } = titleComponentProps;

    const renderSuggestedFacetsComponent = useCallback(
      suggestedFacetsToRender => (
        <SuggestedFacetsComponent
          suggestedFacets={suggestedFacetsToRender}
          onSuggestedFacetClick={onUpdate}
          selectedFacets={selectedFacets}
        />
      ),
      [SuggestedFacetsComponent, onUpdate, selectedFacets]
    );

    const renderStickyFilters = useCallback(
      sticky => (
        <QuickFilters
          suggestedFacets={suggestedFacets}
          facets={facets}
          onSuggestedFacetClick={onUpdate}
          onFiltersBtnClick={onFiltersBtnClick}
          withFiltersBtn
          sticky={sticky}
          activeFiltersCount={activeFiltersCount}
          renderSuggestedFacetsComponent={renderSuggestedFacetsComponent}
        />
      ),
      [activeFiltersCount, facets, onFiltersBtnClick, onUpdate, renderSuggestedFacetsComponent, suggestedFacets]
    );

    return (
      <div className="usurp-sticky-header pos-r mb-1_5" id={SPR_SCROLL_TO_TOP_ANCHOR}>
        <ClippedContainer containerClasses="usurp-header pos-a top-0 left-0 w-100" mask={convexMask} />

        <Container>
          <div
            className={classnames('eve-header-module pos-r bg-white mt-1_5 mx-auto', {
              'p-0_75': isMobile,
              'p-1': !isMobile,
            })}
          >
            <AnimatedSticky
              stickyFragment={renderStickyFilters}
              stickyClassName="py-1"
              disableSticky={!suggestedFacets.length}
            >
              <TitleComponent {...titleComponentProps} isMobile={isMobile} />
              <Container
                className={classnames('pt-1 px-0', {
                  'pt-lg-0': !suggestedFacets.length,
                })}
              >
                <QuickFilters
                  suggestedFacets={suggestedFacets}
                  facets={facets}
                  onSuggestedFacetClick={onUpdate}
                  onFiltersBtnClick={onFiltersBtnClick}
                  withFiltersBtn
                  activeFiltersCount={activeFiltersCount}
                  renderSuggestedFacetsComponent={renderSuggestedFacetsComponent}
                />
              </Container>
            </AnimatedSticky>
          </div>
        </Container>
      </div>
    );
  }
);

UsurpStickyHeader.propTypes = {
  facets: InventoryEntities.Facets,
  selectedFacets: PropTypes.shape({}),
  onFiltersBtnClick: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  suggestedFacets: InventoryEntities.Facets,
  filteringByFacets: PropTypes.arrayOf(PropTypes.shape({})),
  isMobile: PropTypes.bool,
  titleComponent: PropTypes.func.isRequired,
  suggestedFacetsComponent: PropTypes.func.isRequired,
};

UsurpStickyHeader.displayName = 'UsurpStickyHeader';
